import { type RefObject, useEffect, useRef, useState } from "react";

export type PendulumBadgePhase = "swing" | "falling" | "swapped";

interface UsePendulumBadgeResult {
  pivotRef: RefObject<HTMLDivElement | null>;
  badgeRef: RefObject<HTMLDivElement | null>;
  phase: PendulumBadgePhase;
}

export const PENDULUM_INITIAL_ANGLE_DEG = -72;

const REST_ANGLE_DEG = -15;
const GRAVITY = 0.0032;
const DAMPING_PER_SECOND = 0.55;
const SCROLL_IMPULSE_SCALE = 0.09;
const IMPULSE_CLAMP = 0.015;
const MAX_ANGULAR_VELOCITY = 0.04;
const WIND_AMPLITUDE = 0.00006;
const FALL_GRAVITY_PX = 1.2;
const FALL_ROTATION_BOOST_DEG = 0.45;
const MIN_SWING_TIME_MS = 600;
const FALL_TRIGGER_VIEWPORT_RATIO = 0.55;
const FALL_DISTANCE_PX = 420;
const SLEEP_VELOCITY_THRESHOLD = 0.00015;
const IO_ROOT_MARGIN = "0px 0px 20% 0px";

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const REST_ANGLE_RAD = REST_ANGLE_DEG * DEG_TO_RAD;

export function usePendulumBadge(): UsePendulumBadgeResult {
  const pivotRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<PendulumBadgePhase>("swing");

  useEffect(() => {
    const badge = badgeRef.current;
    const pivot = pivotRef.current;
    if (!badge || !pivot) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setPhase("swapped");
      return;
    }

    let currentPhase: PendulumBadgePhase = "swing";
    let angleRad = PENDULUM_INITIAL_ANGLE_DEG * DEG_TO_RAD;
    let angularVelocity = 0;
    let translateY = 0;
    let fallVelocity = 0;
    let opacity = 1;
    let lastScrollY = window.scrollY;
    let lastFrameTime = performance.now();
    let firstVisibleAt: number | null = null;
    let isInView = false;
    let rafId: number | null = null;

    const applyTransform = () => {
      const angleDeg = angleRad * RAD_TO_DEG;
      badge.style.transform = `translateY(${translateY.toFixed(2)}px) rotate(${angleDeg.toFixed(3)}deg)`;
      badge.style.opacity = opacity.toFixed(3);
    };

    applyTransform();

    const scheduleFrame = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(frame);
    };

    function frame(now: number) {
      if (!badge) return;
      rafId = null;
      const rawDt = (now - lastFrameTime) / (1000 / 60);
      const dt = Math.max(0.5, Math.min(2, rawDt));
      lastFrameTime = now;

      if (currentPhase === "swing") {
        const rect = badge.getBoundingClientRect();
        const vh = window.innerHeight;
        const centerY = rect.top + rect.height / 2;

        if (isInView && firstVisibleAt === null) {
          firstVisibleAt = now;
        }

        const dy = window.scrollY - lastScrollY;
        lastScrollY = window.scrollY;

        const scrollVelNormalized = dy / vh;
        const positionalWeight = Math.max(0, 1 - 2 * Math.abs(0.5 - centerY / vh));
        const rawImpulse = scrollVelNormalized * SCROLL_IMPULSE_SCALE * positionalWeight;
        const impulse = Math.max(-IMPULSE_CLAMP, Math.min(IMPULSE_CLAMP, rawImpulse));

        const wind = Math.sin(now * 0.0008) * WIND_AMPLITUDE;
        const restoring = -GRAVITY * Math.sin(angleRad - REST_ANGLE_RAD);
        const angularAccel = restoring + impulse + wind;

        angularVelocity += angularAccel * dt;
        angularVelocity = Math.max(
          -MAX_ANGULAR_VELOCITY,
          Math.min(MAX_ANGULAR_VELOCITY, angularVelocity),
        );
        angularVelocity *= DAMPING_PER_SECOND ** (dt / 60);
        angleRad += angularVelocity * dt;

        applyTransform();

        const canFall =
          isInView &&
          centerY / vh < FALL_TRIGGER_VIEWPORT_RATIO &&
          firstVisibleAt !== null &&
          now - firstVisibleAt >= MIN_SWING_TIME_MS;

        if (canFall) {
          currentPhase = "falling";
          setPhase("falling");
          scheduleFrame();
          return;
        }

        if (!isInView && Math.abs(angularVelocity) < SLEEP_VELOCITY_THRESHOLD) {
          return;
        }
        scheduleFrame();
      } else if (currentPhase === "falling") {
        fallVelocity += FALL_GRAVITY_PX * dt;
        translateY += fallVelocity * dt;
        const spinDir = Math.sign(angularVelocity) || -1;
        angleRad += FALL_ROTATION_BOOST_DEG * spinDir * DEG_TO_RAD * dt;
        opacity = Math.max(0, 1 - translateY / FALL_DISTANCE_PX);

        applyTransform();

        if (opacity <= 0) {
          currentPhase = "swapped";
          setPhase("swapped");
          return;
        }
        scheduleFrame();
      }
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        const wasInView = isInView;
        isInView = entry.isIntersecting;
        if (!wasInView && isInView && currentPhase === "swing") {
          lastScrollY = window.scrollY;
          lastFrameTime = performance.now();
          scheduleFrame();
        }
      },
      { threshold: 0, rootMargin: IO_ROOT_MARGIN },
    );
    io.observe(pivot);

    const onScroll = () => {
      if (currentPhase === "swing" && isInView) {
        scheduleFrame();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  return { pivotRef, badgeRef, phase };
}
